import React, { useEffect, useState } from "react";
import type { Article } from "../types/Article";

interface UnsavedChangesGuardProps {
  form: Article;
  previewImages: any[];
  handleSubmit: (e: React.FormEvent) => Promise<string | undefined>;
  getInitialFormState: () => Article;
  modalOverlayStyle: React.CSSProperties;
  modalStyle: React.CSSProperties;
  buttonPrimary: React.CSSProperties;
  buttonDanger: React.CSSProperties;
}

const UnsavedChangesGuard: React.FC<UnsavedChangesGuardProps> = ({
  form,
  previewImages,
  handleSubmit,
  getInitialFormState,
  modalOverlayStyle,
  modalStyle,
  buttonPrimary,
  buttonDanger,
}) => {
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Helper: check if form is dirty (has unsaved changes)
  const isFormDirty = () => {
    const initial = getInitialFormState();
    for (const key in initial) {
      if (key === "imageUrls") {
        if ((form.imageUrls ?? []).length > 0) return true;
      } else if ((form[key as keyof Article] ?? "") !== (initial[key as keyof Article] ?? "")) {
        return true;
      }
    }
    return false;
  };

  // Prevent browser reload/close if form is dirty
  useEffect(() => {
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (isFormDirty()) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return "";
      }
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
    return () => window.removeEventListener("beforeunload", beforeUnloadHandler);
  }, [form, previewImages]);

  // Prevent navigation within SPA (back, route change)
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (isFormDirty()) {
        e.preventDefault();
        setShowUnsavedModal(true);
        setPendingNavigation(() => () => window.history.back());
        window.history.pushState(null, "", window.location.href); // Prevent navigation
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [form, previewImages]);

  return (
    <>
      {showUnsavedModal && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <p style={{ fontSize: "1.1rem", marginBottom: "24px" }}>
              You have unsaved changes. Do you want to save before leaving?
            </p>
            <div style={{ display: "flex", gap: "16px" }}>
              <button
                style={{ ...buttonPrimary, padding: "8px 24px" }}
                onClick={async () => {
                  await handleSubmit(new Event("submit") as any);
                  setShowUnsavedModal(false);
                  if (pendingNavigation) pendingNavigation();
                }}
              >
                Save & Leave
              </button>
              <button
                style={{ ...buttonDanger, padding: "8px 24px" }}
                onClick={() => {
                  setShowUnsavedModal(false);
                  if (pendingNavigation) pendingNavigation();
                }}
              >
                Leave without saving
              </button>
              <button
                style={{ ...buttonPrimary, padding: "8px 24px" }}
                onClick={() => setShowUnsavedModal(false)}
              >
                Stay
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnsavedChangesGuard;
